import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {Tracker} from "../../../interfaces/Tracker";
import {Plant} from "../../../interfaces/Plant";
import {ImmersionHeater} from "../../../interfaces/immersion_heater/ImmersionHeater";
import {Meter} from "../../../interfaces/Meter";
import {SolarGateway} from "../../../interfaces/solar_gateway/SolarGateway";
import {Ess} from "../../../interfaces/ess/Ess";
import apiV2 from "../../../service/api/ApiV2";
import {EntityTypeEnum} from "../../../enums/EntityTypeEnum";
import {InterventionDate} from "../../../interfaces/plant/InterventionDate";
import ToastService from "../../../service/ToastService";
import {getErrorMessage} from "../../../service/AuthenticatedApi";
import {SetPlantConsumptionLevel} from "../../../interfaces/plant/SetPlantConsumptionLevel";
import {SetPlantPowerFactor} from "../../../interfaces/plant/SetPlantPowerFactor";
import apiEms from "../../../service/api/ApiEms";
import {OffPeakEnergyStrategy} from "../../../interfaces/ems/PlantEnergyStrategy";
import i18next from "i18next";

export interface PlantObjects {
  plant: Plant;
  trackers: {isSelected: boolean; tracker: Tracker}[];
  immersionHeaters: {isSelected: boolean; immersionHeater: ImmersionHeater}[];
  solarGateways: {isSelected: boolean; solarGateway: SolarGateway}[];
  ess: {isSelected: boolean; ess: Ess}[];
  meter?: {isSelected: boolean; meter: Meter};
  offPeakStrategy?: OffPeakEnergyStrategy;
}

interface PlantObjectsState {
  value: PlantObjects | null;
}

const initialState: PlantObjectsState = {
  value: null,
};

export const updatePlantInterventionDate = createAsyncThunk<
  {interventionDate: InterventionDate},
  InterventionDate,
  {rejectValue: string | null}
>("plant/updatePlantInterventionDate", async (interventionDate, {rejectWithValue}) => {
  try {
    await apiV2.updateEntityProperty(EntityTypeEnum.PLANT, interventionDate.plantId, [
      {name: "interventionDate", value: interventionDate.interventionDate},
    ]);
    return {interventionDate};
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, i18next.t("scada:plant.updates.interventionDateError")));
  }
});

export const updatePlantConsumptionLevel = createAsyncThunk<
  {consumptionLevel: SetPlantConsumptionLevel},
  SetPlantConsumptionLevel,
  {rejectValue: string | null}
>("plant/updatePlantConsumptionLevel", async (consumptionLevel, {rejectWithValue}) => {
  try {
    await apiV2.updateEntityProperty(EntityTypeEnum.PLANT, consumptionLevel.plantId, [
      {name: "consumptionLevel", value: consumptionLevel.consumptionLevel},
    ]);
    return {consumptionLevel};
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, i18next.t("scada:plant.updates.consumptionLevelError")));
  }
});

export const updatePlantPowerFactor = createAsyncThunk<
  {response: Response; powerFactor: SetPlantPowerFactor},
  SetPlantPowerFactor,
  {rejectValue: string | null}
>("plant/updateTrackerPowerFactor", async (powerFactor, {rejectWithValue}) => {
  try {
    const response = await apiV2.updatePlantPowerFactor(powerFactor);
    return {response, powerFactor};
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, i18next.t("scada:plant.updates.powerFactorError")));
  }
});

export const updatePlantOffPeakStrategy = createAsyncThunk(
  "plant/updatePlantOffPeakStrategy",
  async (strategy: OffPeakEnergyStrategy) => {
    const response = await apiEms.updateOffPeakStrategy(strategy);
    return {response, strategy};
  },
);

const plantObjectsSlice = createSlice({
  name: "plantObjects",
  initialState,
  reducers: {
    toggleTracker: (state, action) => {
      if (state.value === null) return;
      const {id, isSelected} = action.payload;
      state.value.trackers = state.value?.trackers.map((x) =>
        x.tracker.id === id ? {isSelected: isSelected, tracker: x.tracker} : x,
      );
    },
    toggleImmersionHeater: (state, action) => {
      if (state.value === null) return;
      const {id, isSelected} = action.payload;
      state.value.immersionHeaters = state.value.immersionHeaters.map((x) =>
        x.immersionHeater.id === id ? {isSelected: isSelected, immersionHeater: x.immersionHeater} : x,
      );
    },
    toggleEss: (state, action) => {
      if (state.value === null) return;
      const {id, isSelected} = action.payload;
      state.value.ess = state.value.ess.map((x) => (x.ess.id === id ? {isSelected: isSelected, ess: x.ess} : x));
    },
    toggleSolarGateway: (state, action) => {
      if (state.value === null) return;
      const {id, isSelected} = action.payload;
      state.value.solarGateways = state.value.solarGateways.map((x) =>
        x.solarGateway.id === id ? {isSelected: isSelected, solarGateway: x.solarGateway} : x,
      );
    },
    toggleMeter: (state, action) => {
      if (state.value === null) return;
      if (state.value.meter) state.value.meter.isSelected = action.payload.isSelected;
    },
    toggleAll: (state, action) => {
      if (state.value === null) return;
      state.value.trackers = state.value.trackers.map((x) => {
        return {tracker: x.tracker, isSelected: action.payload.isSelected};
      });
      state.value.immersionHeaters = state.value.immersionHeaters.map((x) => {
        return {
          immersionHeater: x.immersionHeater,
          isSelected: action.payload.isSelected,
        };
      });
      state.value.ess = state.value.ess.map((x) => {
        return {ess: x.ess, isSelected: action.payload.isSelected};
      });
      state.value.solarGateways = state.value.solarGateways.map((x) => {
        return {solarGateway: x.solarGateway, isSelected: action.payload.isSelected};
      });
      if (state.value.meter) state.value.meter.isSelected = action.payload.isSelected;
    },
    setPlantObjects: (state, action) => {
      state.value = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updatePlantInterventionDate.fulfilled, (state, action) => {
        const {interventionDate} = action.payload;
        if (state.value !== null) {
          state.value.plant.interventionDate = interventionDate.interventionDate;
        }
        ToastService.success(i18next.t("scada:plant.updates.interventionDate"));
      })
      .addCase(updatePlantConsumptionLevel.fulfilled, (state, action) => {
        const {consumptionLevel} = action.payload;
        if (state.value !== null) {
          state.value.plant.consumptionLevel = consumptionLevel.consumptionLevel;
        }
        ToastService.success(i18next.t("scada:plant.updates.consumptionLevel"));
      })
      .addCase(updatePlantPowerFactor.fulfilled, (state, action) => {
        const {powerFactor} = action.payload;
        if (state.value !== null) {
          state.value.plant.powerFactor = powerFactor.powerFactor;
        }
        ToastService.success(i18next.t("scada:plant.updates.powerFactor"));
      })
      .addCase(updatePlantOffPeakStrategy.rejected, (state, action) => {
        const error = action.error.message || i18next.t("scada:plant.updates.thresholdError");
        ToastService.error(error);
      })
      .addCase(updatePlantOffPeakStrategy.fulfilled, (state, action) => {
        const {strategy} = action.payload;
        if (state.value !== null) {
          state.value.offPeakStrategy = strategy;
        }
        ToastService.success(i18next.t("scada:plant.updates.ems"));
      });
  },
});

export const {
  setPlantObjects,
  toggleTracker,
  toggleImmersionHeater,
  toggleSolarGateway,
  toggleEss,
  toggleMeter,
  toggleAll,
} = plantObjectsSlice.actions;

export default plantObjectsSlice.reducer;
